import { getJourneySpecialistRoleMapping } from './journeySpecialistRoleMappings.js';

export const JOURNEY_ROLE_EXCLUSIONS = Object.freeze({
  maternity: Object.freeze(['bereavement_officer', 'chaplain']),
  radiotherapy: Object.freeze(['dosimetrist']),
});

// Gold-standard role ordering. These are canonical role keys and therefore
// resolve through the same Trust-specific uniform system as the rest of KYC.
export const JOURNEY_ROLE_OVERRIDES = Object.freeze({
  'day-surgery': Object.freeze([
    'pre_assessment_nurse',
    'surgeon',
    'anaesthetist',
    'operating_department_practitioner',
    'theatre_nurse',
    'theatre_recovery_nurse',
    'healthcare_assistant',
    'hospital_porter',
    'pharmacist',
    'physiotherapist',
  ]),
});

const RESIDENT_DOCTOR_ROLE_KEY = 'resident_doctor_previously_referred_to_as_junior_doctor';

// Some early generic Journey content used friendly specialty labels that were
// never canonical role keys. Normalise them here so they cannot silently drop
// out of the Journey renderer or bypass the shared role/uniform library.
const JOURNEY_ROLE_ALIASES = Object.freeze({
  cardiologist: 'consultant',
  cardiac_specialist_nurse: 'clinical_nurse_specialist',
  stroke_consultant: 'consultant',
  stroke_nurse: 'clinical_nurse_specialist',
  orthopaedic_surgeon: 'surgeon',
  neurologist: 'consultant',
  neurosurgeon: 'surgeon',
  nephrologist: 'consultant',
  renal_nurse: 'clinical_nurse_specialist',
  dialysis_nurse: 'staff_nurse',
  respiratory_consultant: 'consultant',
  respiratory_nurse: 'clinical_nurse_specialist',
  lung_function_physiologist: 'respiratory_physiologist',
  psychologist: 'clinical_psychologist',
  endoscopist: 'consultant',
  endoscopy_nurse: 'staff_nurse',
  clinical_oncologist: 'consultant',
  medical_oncologist: 'consultant',
  chemotherapy_nurse: 'clinical_nurse_specialist',
});

const JOURNEY_ROLE_ADDITIONS = Object.freeze({
  cardiology: Object.freeze(['consultant', RESIDENT_DOCTOR_ROLE_KEY]),
  stroke: Object.freeze(['consultant', RESIDENT_DOCTOR_ROLE_KEY]),
  orthopaedics: Object.freeze(['consultant', RESIDENT_DOCTOR_ROLE_KEY]),
  renal: Object.freeze(['consultant', RESIDENT_DOCTOR_ROLE_KEY]),
  respiratory: Object.freeze(['consultant', RESIDENT_DOCTOR_ROLE_KEY]),
  'surgery-operation': Object.freeze(['pre_assessment_nurse']),
  'cancer-care': Object.freeze([RESIDENT_DOCTOR_ROLE_KEY]),
});

function canonicalRoleId(role) {
  const id = typeof role === 'string' ? role : role?.id;
  return getJourneySpecialistRoleMapping(id)?.parentRoleKey || JOURNEY_ROLE_ALIASES[id] || id;
}

function canonicaliseJourneyRoles(roles = []) {
  const seen = new Set();
  const result = [];
  for (const role of roles) {
    const id = canonicalRoleId(role);
    if (!id || seen.has(id)) continue;
    seen.add(id);
    const rawId = typeof role === 'string' ? role : role?.id;
    const specialist = getJourneySpecialistRoleMapping(rawId);
    const original = typeof role === 'string' ? {} : role;
    result.push({
      ...original,
      id,
      ...(specialist && rawId !== id ? {
        requestedRoleKey: rawId,
        // Explicit provenance allows the renderer and reviewer to distinguish
        // an intentional national parent card from a real specialty variant.
        specialistParentRoleKey: id,
        ...(specialist.variantKey ? { variantKey: specialist.variantKey } : {}),
      } : {}),
    });
  }
  return result;
}

function addJourneyRoles(templateId, roles = []) {
  const additions = JOURNEY_ROLE_ADDITIONS[templateId] || [];
  if (!additions.length) return roles;
  const existing = new Set(roles.map((role) => typeof role === 'string' ? role : role?.id).filter(Boolean));
  return [...roles, ...additions.filter((id) => !existing.has(id)).map((id) => ({ id }))];
}

export function applyJourneyRoleQuality(templateId, roles = []) {
  const override = JOURNEY_ROLE_OVERRIDES[templateId];
  if (override?.length) return override.map((id) => ({ id }));

  const excluded = new Set(JOURNEY_ROLE_EXCLUSIONS[templateId] || []);
  const filtered = excluded.size
    ? roles.filter((role) => {
        const id = typeof role === 'string' ? role : role?.id;
        return id && !excluded.has(id);
      })
    : roles;

  return addJourneyRoles(templateId, canonicaliseJourneyRoles(filtered));
}
